// @ts-nocheck
import React, { useLayoutEffect, useRef, useState } from 'react';
import { sx } from '../sx';
import { SEGMENTS, SPLIT, pill } from '../controls';
import InfoTip from './InfoTip';
import CohortSwitch from './CohortSwitch';

const COUNT = (fg, active) => `flex:none;height:28px;padding:0 ${active ? 10 : 2}px;border:${active ? '1.5px solid ' + fg : 'none'};border-radius:999px;background:none;font-family:inherit;font-size:12px;font-weight:500;color:${fg};cursor:pointer;text-decoration:${active ? 'none' : 'underline dotted'};text-underline-offset:3px`;
const LABEL = 'flex:none;font-size:12px;color:var(--ink-3)';

function MethodOption({ o }) {
  return (
    <div style={sx(`display:flex;align-items:center;gap:6px;border-radius:8px;background:${o.selected ? '#EAF1FD' : 'transparent'}`)}>
      <button
        type="button"
        role="menuitemradio"
        aria-checked={!!o.selected}
        className="gmHover"
        onClick={o.onPick}
        style={sx(`flex:1;min-width:0;height:30px;padding:0 10px;border:none;border-radius:8px;background:none;text-align:left;font-family:inherit;font-size:12px;font-weight:${o.selected ? 500 : 400};color:${o.selected ? '#0052CD' : o.group === 'other' ? 'var(--ink-3)' : 'var(--ink)'};cursor:pointer`)}
        data-hover="background:#EAF1FD"
      >
        {o.label}
      </button>
      <span style={sx('flex:none;padding-right:8px')}><InfoTip text={o.tip} label={o.label} /></span>
    </div>
  );
}

const POP_LABEL = 'margin:0;font-size:14px;font-weight:600;color:var(--ink)';
const POP_FIELD = 'box-sizing:border-box;width:100%;height:38px;margin-top:10px;padding:0 12px;border-radius:8px;font-family:inherit;font-size:13px;color:var(--ink);outline:none';

function PopLabel({ label, tip }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:6px')}>
      <p style={sx(POP_LABEL)}>{label}</p>
      <InfoTip text={tip} label={label} />
    </div>
  );
}

const SCREEN_GAP = 16;

function RetentionPopover({ r }) {
  const [text, setText] = useState(r.change.value);
  const [start, setStart] = useState(r.start.value);
  const [bad, setBad] = useState(false);
  const [left, setLeft] = useState(0);
  const box = useRef(null);
  const apply = () => setBad(!r.onApply(text, start));
  useLayoutEffect(() => {
    const place = () => {
      const el = box.current;
      if (!el || !el.parentElement) return;
      const anchor = el.parentElement.getBoundingClientRect().left;
      setLeft(Math.max(SCREEN_GAP - anchor, Math.min(0, window.innerWidth - SCREEN_GAP - anchor - el.offsetWidth)));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, []);
  return (
    <div ref={box} data-pop="retention" role="dialog" aria-label="Retention change" data-testid="cohort-retention-pop" style={sx(`position:absolute;top:34px;left:${left}px;z-index:40;width:476px;max-width:calc(100vw - ${SCREEN_GAP * 2}px);box-sizing:border-box;padding:18px;border:1px solid #DCE4F2;border-radius:12px;background:#fff;box-shadow:0 30px 50px -25px rgba(8,35,76,0.35)`)}>
      <div data-testid="cohort-retention-change">
        <PopLabel label={r.change.label} tip={r.change.desc} />
        <input
          className="gmFocus"
          aria-label={r.change.label}
          aria-invalid={bad || undefined}
          value={text}
          disabled={r.disabled}
          placeholder={r.change.placeholder}
          onChange={e => { setText(e.target.value); setBad(false); }}
          onKeyDown={e => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') r.onClose(); }}
          style={sx(`${POP_FIELD};border:1px solid ${bad ? '#E5484D' : '#C9D0DD'};background:${r.disabled ? '#F5F6FA' : '#fff'}`)}
        />
        {bad ? <p role="alert" style={sx(`margin:6px 0 0;font-size:12px;color:#B42318`)}>Enter a change between −99% and +1000%, for example +5% or −10%.</p> : null}
      </div>
      <div data-testid="cohort-retention-start" style={sx(`margin-top:18px;padding-top:18px;border-top:1px solid #EDF1F8`)}>
        <PopLabel label={r.start.label} tip={r.start.desc} />
        <select
          className="gmFocus"
          aria-label={r.start.label}
          value={start === null ? '' : String(start)}
          disabled={r.disabled}
          onChange={e => setStart(Number(e.target.value))}
          style={sx(`${POP_FIELD};border:1px solid #C9D0DD;background:${r.disabled ? '#F5F6FA' : '#fff'};cursor:${r.disabled ? 'default' : 'pointer'}`)}
        >
          {r.start.options.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
        </select>
      </div>
      <div style={sx(`display:flex;justify-content:flex-end;gap:8px;margin-top:18px`)}>
        {r.disabled ? null : (
          <button
            type="button"
            data-testid="cohort-retention-reset"
            disabled={!r.canReset}
            onClick={r.onReset}
            className="gmHover"
            data-hover={r.canReset ? 'border-color:#0052CD;color:#0052CD' : ''}
            style={sx(`margin-right:auto;height:34px;padding:0 14px;border:1px solid #C8D4EA;border-radius:999px;background:#fff;font-family:inherit;font-size:12.5px;color:${r.canReset ? 'var(--ink-2)' : 'var(--ink-4)'};cursor:${r.canReset ? 'pointer' : 'default'}`)}
          >
            Reset
          </button>
        )}
        <button type="button" onClick={r.onClose} style={sx(`height:34px;padding:0 14px;border:1px solid #C8D4EA;border-radius:999px;background:#fff;font-family:inherit;font-size:12.5px;color:var(--ink-2);cursor:pointer`)}>Cancel</button>
        {r.disabled ? null : (
          <button type="button" onClick={apply} style={sx(`height:34px;padding:0 16px;border:none;border-radius:999px;background:#0052CD;font-family:inherit;font-size:12.5px;font-weight:500;color:#fff;cursor:pointer`)}>Apply</button>
        )}
      </div>
    </div>
  );
}

function StepButton({ label, symbol, enabled, onClick }) {
  return (
    <button
      type="button"
      className="gmHover"
      aria-label={label}
      disabled={!enabled}
      onClick={onClick}
      style={sx(`flex:none;width:28px;height:28px;border:none;background:none;font-family:inherit;font-size:15px;line-height:1;color:${enabled ? '#0052CD' : '#C8D4EA'};cursor:${enabled ? 'pointer' : 'default'}`)}
      data-hover={enabled ? 'background:#EAF1FD' : ''}
    >
      {symbol}
    </button>
  );
}

export default function CohortForecastRow({ f }: { f: any }) {
  const [changedOpen, setChangedOpen] = useState(false);
  const seen = useRef(false);
  if (!f) return null;
  const closeChanged = () => {
    setChangedOpen(false);
    if (seen.current && f.autoChanged) f.autoChanged.onSeen();
  };
  return (
    <>
      {f.fixedNote ? (
        <div role="status" data-testid="cohort-fixed-note" style={sx(`flex:none;display:flex;align-items:center;gap:10px;padding:7px 20px;background:#FFF6DD;border-bottom:1px solid #F1D48A;font-size:12px;color:#7A5200`)}>
          <span style={sx('flex:1;min-width:0')}>{f.fixedNote}</span>
          <button type="button" aria-label="Dismiss" onClick={f.onFixedNoteClose} style={sx(`flex:none;border:none;background:none;font-family:inherit;font-size:14px;line-height:1;color:#7A5200;cursor:pointer`)}>×</button>
        </div>
      ) : null}
      <div data-testid="cohort-forecast-row" style={sx(`flex:none;display:flex;align-items:center;flex-wrap:wrap;gap:8px 12px;padding:8px 20px;background:#F7FAFD;border-top:1px solid #DCE4F2;border-bottom:1px solid #DCE4F2`)}>
        <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:6px`)}>
          <span style={sx(`${LABEL};font-weight:500;color:var(--ink-2)`)}>Forecast</span>
          <InfoTip text={f.forecastTip} label="Forecast" />
        </span>
        <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:6px`)}>
          <button
            type="button"
            data-testid="cohort-auto"
            aria-pressed={!!f.autoOn}
            disabled={f.autoDisabled && !f.autoOn}
            onClick={f.onAuto}
            style={sx(`${pill(f.autoOn ? '#0052CD' : '#C8D4EA')};background:${f.autoOn ? '#0052CD' : '#fff'};color:${f.autoOn ? '#fff' : f.autoDisabled ? 'var(--ink-4)' : 'var(--ink-2)'};font-weight:${f.autoOn ? 500 : 400};cursor:${f.autoDisabled ? 'default' : 'pointer'}`)}
          >
            {f.autoLabel}
          </button>
          <InfoTip text={f.autoTip} label="Auto" />
        </span>
        {f.autoChanged ? (
          <span style={sx('position:relative;flex:none;display:inline-flex')}>
            <button
              type="button"
              data-testid="cohort-auto-changed"
              aria-describedby="cohort-auto-changed-tip"
              onMouseEnter={() => { seen.current = true; setChangedOpen(true); }}
              onMouseLeave={closeChanged}
              onFocus={() => { seen.current = true; setChangedOpen(true); }}
              onBlur={closeChanged}
              style={sx(`${COUNT('#0052CD')};font-weight:400;cursor:help`)}
            >
              Auto changed
            </button>
            {changedOpen ? (
              <span id="cohort-auto-changed-tip" role="tooltip" style={sx(`position:absolute;top:32px;left:-12px;z-index:30;width:320px;padding:8px 10px;border-radius:8px;background:#1f2937;color:#fff;font-size:12px;line-height:1.5;box-shadow:0 8px 24px rgba(8,35,76,0.25)`)}>{f.autoChanged.tip}</span>
            ) : null}
          </span>
        ) : null}
        <div style={sx(SPLIT)}></div>
        {f.newerLabel ? <span data-testid="cohort-newer-label" style={sx(`${LABEL};font-weight:500;color:var(--ink-2)`)}>{f.newerLabel}</span> : null}
        <span data-pop="forecastMenu" style={sx(`position:relative;flex:none;display:inline-flex;align-items:center;gap:6px`)}>
          <button
            type="button"
            data-testid="cohort-forecast-menu"
            aria-haspopup="menu"
            aria-expanded={!!f.menuOpen}
            disabled={f.disabled}
            onClick={f.onMenu}
            className="gmHover"
            style={sx(`${pill()};max-width:280px;cursor:${f.disabled ? 'default' : 'pointer'}`)}
            data-hover={f.disabled ? '' : 'border-color:#0052CD'}
          >
            <span style={sx('overflow:hidden;text-overflow:ellipsis')}>{f.methodLabel}</span>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="var(--ink-4)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l3 3 3-3"></path></svg>
          </button>
          <InfoTip text={f.methodTip} label="Forecast method" />
          {f.menuOpen ? (
            <div role="menu" aria-label="Forecast method" style={sx(`position:absolute;top:34px;left:0;z-index:40;width:300px;padding:6px;border:1px solid #DCE4F2;border-radius:12px;background:#fff;box-shadow:0 30px 50px -25px rgba(8,35,76,0.25)`)}>
              <div style={sx(`padding:6px 10px 4px;font-size:10px;font-weight:500;letter-spacing:0.12em;text-transform:uppercase;color:var(--ink-4)`)}>{f.mainTitle}</div>
              {(f.main || []).map(o => <MethodOption key={o.id} o={o} />)}
              <div style={sx(`height:1px;margin:6px 4px;background:#EDF1F8`)}></div>
              <div style={sx(`padding:2px 10px 4px;font-size:10px;font-weight:500;letter-spacing:0.12em;text-transform:uppercase;color:var(--ink-4)`)}>{f.otherTitle}</div>
              {(f.other || []).map(o => <MethodOption key={o.id} o={o} />)}
              {f.menuFooter ? (
                <>
                  <div style={sx(`height:1px;margin:6px 4px;background:#EDF1F8`)}></div>
                  <div data-testid="cohort-menu-footer" style={sx(`display:flex;align-items:center;gap:6px;padding:6px 10px;font-size:12px;color:var(--ink-3)`)}>
                    <span style={sx('flex:1;min-width:0')}>{f.menuFooter.text}</span>
                    <InfoTip text={f.menuFooter.tip} label="Older cohorts" />
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </span>
        {f.showN ? (
          <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:6px`)}>
            <div className="gmField" style={sx(`${SEGMENTS};align-items:center`)}>
              <StepButton label="fewer cohorts" symbol="−" enabled={f.canDec} onClick={f.onDec} />
              <span data-testid="cohort-n" style={sx(`min-width:18px;text-align:center;font-size:12px;font-weight:600;font-variant-numeric:tabular-nums;color:var(--ink)`)}>{f.nText}</span>
              <StepButton label="more cohorts" symbol="+" enabled={f.canInc} onClick={f.onInc} />
              <span style={sx(`flex:none;padding-right:12px;font-size:12px;color:var(--ink-3)`)}>cohorts</span>
            </div>
            <InfoTip text={f.nTip} label="Cohorts" />
          </span>
        ) : null}
        <div style={sx(SPLIT)}></div>
        <CohortSwitch label="Monthly seasonality" testId="cohort-season" on={f.season.on} disabled={f.season.disabled} tip={f.season.tip} onToggle={f.season.onToggle} />
        {f.trend ? <CohortSwitch label="Year on year trend" testId="cohort-trend" on={f.trend.on} disabled={f.trend.disabled} tip={f.trend.tip} onToggle={f.trend.onToggle} /> : null}
        {f.retention ? (
          <span data-pop="retention" style={sx(`position:relative;flex:none;display:inline-flex;align-items:center;gap:6px`)}>
            <button type="button" data-testid="cohort-retention" aria-haspopup="dialog" aria-expanded={!!f.retention.open} onClick={f.retention.onOpen} style={sx(`${pill(f.retention.on ? '#0052CD' : '#C8D4EA')};color:${f.retention.on ? '#0052CD' : 'var(--ink-3)'};font-weight:${f.retention.on ? 500 : 400}`)}>{f.retention.text}</button>
            <InfoTip text={f.retention.tip} label="Retention change" />
            {f.retention.open ? <RetentionPopover r={f.retention} /> : null}
          </span>
        ) : null}
        {f.trendPill ? (
          <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:6px`)}>
            <span data-testid="cohort-trend-pill" style={sx(`flex:none;font-size:11.5px;font-weight:500;color:${f.trendHeld ? 'var(--ink-4)' : '#0052CD'};font-variant-numeric:tabular-nums`)}>{f.trendPill}</span>
            <InfoTip text={f.trendPillTip} label="Trend" />
          </span>
        ) : null}
        <span style={sx(`flex:1;min-width:12px`)}></span>
        {f.openText ? (
          <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:4px`)}>
            <button type="button" data-testid="cohort-open-flags" aria-pressed={!!f.openActive} onClick={f.onOpenFlags} style={sx(COUNT('#C0262D', f.openActive))}>{f.openText}</button>
            <InfoTip text={f.openTip} label="To review" />
          </span>
        ) : null}
        {f.acceptedText ? (
          <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:4px`)}>
            <button type="button" data-testid="cohort-accepted-flags" aria-pressed={!!f.acceptedActive} onClick={f.onAcceptedFlags} style={sx(COUNT('#0F7A55', f.acceptedActive))}>{f.acceptedText}</button>
            <InfoTip text={f.acceptedTip} label="Accepted" />
          </span>
        ) : null}
        {f.overridesText ? (
          <span style={sx(`flex:none;display:inline-flex;align-items:center;gap:4px`)}>
            <button type="button" data-testid="cohort-overrides" aria-pressed={!!f.overridesActive} onClick={f.onOverrides} style={sx(COUNT('#0052CD', f.overridesActive))}>{f.overridesText}</button>
            <InfoTip text={f.overridesTip} label="Overrides" />
          </span>
        ) : null}
      </div>
    </>
  );
}
